// Angular
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';

// Ionic
import {
  AlertController,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonTitle,
  IonToolbar,
  IonSelect,
  IonSelectOption,
  ModalController,
} from '@ionic/angular/standalone';

// Local library
import {
  AllowedNumbersDirective,
  DecimalFormatterDirective,
  DecimalInputDirective,
  UnsubscribeObservableService,
} from '@amwager/util';
import { CommonFacade, SessionService } from '@amwager/data-access';

// Third party
import * as _ from 'lodash';

// Local file
import { DebitCreditCardComponent } from '../deposit/debit-credit-card/debit-credit-card.component';
import { PromoCodeComponent } from '../deposit/promo-code/promo-code.component';
import { MoneyPakComponent } from '../deposit/money-pak/money-pak.component';
import { PayNearMeComponent } from '../deposit/pay-near-me/pay-near-me.component';
import { WireTransferComponent } from '../deposit/wire-transfer/wire-transfer.component';
import { CryptoCurrencyComponent } from '../deposit/crypto-currency/crypto-currency.component';
import { OnlineCheckComponent } from '../deposit/online-check/online-check.component';

import {
  DebitCreditCardComponent as WithdrawDebitCreditCardComponent
} from '../withdraw/debit-credit-card/debit-credit-card.component';
import {
  WireTransferComponent as WithdrawWireTransferComponent
} from '../withdraw/wire-transfer/wire-transfer.component';
import {
  CheckByEmailComponent as WithdrawCheckByEmailComponent
} from '../withdraw/check-by-email/check-by-email.component';

import {
  OnlineCheckComponent as WithdrawOnlineCheckComponent
} from '../withdraw/online-check/online-check.component';
import { AuthFacade } from '@amwager/authentication/data-access';

@Component({
  selector: 'feature-ion-funding',
  templateUrl: './funding.component.html',
  styleUrls: ['./funding.component.scss'],
  imports: [
    IonHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonTitle,
    IonContent,
    IonItem,
    IonInput,
    IonIcon,
    IonSelect,
    IonSelectOption,
    DecimalFormatterDirective,
    DecimalInputDirective,
    AllowedNumbersDirective,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    DebitCreditCardComponent,
    PromoCodeComponent,
    MoneyPakComponent,
    PayNearMeComponent,
    WireTransferComponent,
    CryptoCurrencyComponent,
    OnlineCheckComponent,
    WithdrawDebitCreditCardComponent,
    WithdrawWireTransferComponent,
    WithdrawCheckByEmailComponent,
    WithdrawOnlineCheckComponent
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FundingComponent extends UnsubscribeObservableService
  implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(PayNearMeComponent) payNearMeComponent!: PayNearMeComponent;

  showDepositList!: boolean;
  showWithdrawList!: boolean;

  fundingTabList!: HTMLElement[];
  cardContentHTMList!: HTMLElement[];

  orderId?: string;
  cardNumber!: string;
  ccv!: number;
  months!: string[];
  dates!: number[];
  showCardList = false;
  showAddCard: boolean = false;
  affiliateName!: string;

  controller = {
    debitAndCreditCard: false,
    cryptoCurrency: false,
    wireTransfer: false,
    payNearMe: false,
    moneyPak: false,
    onlineCheck: false,
    nhPlay: false,
    checkMoneyOrder: false,
    promoCode: false,
    checkByEmail: false
  };

  private showAlertInfoHandler?: HTMLIonAlertElement;

  constructor(
    private modalCtrl: ModalController,
    private elementRef: ElementRef,
    private readonly alertCtrl: AlertController,
    private readonly sessionService: SessionService,
    private readonly commonFacade: CommonFacade,
    private readonly authFacade: AuthFacade,
  ) {
    super();

    this.authFacade.isLogoutSuccess$().subscribe((isLogout?: boolean) => {
      if (isLogout) {
        this.modalCtrl?.dismiss();

        this.alertCtrl?.dismiss();
        this.showAlertInfoHandler?.dismiss();
      }
    });
  }

  ngOnInit() {
    this.showDepositList = true;
    this.showWithdrawList = false;
    this.affiliateName = this.sessionService.getAffiliateName();
  }

  ngAfterViewInit() {

    // hide card-content
    this.cardContentHTMList = this.elementRef.nativeElement.querySelectorAll(
      '.my-deposit-section .card-content'
    );
    _.forEach(this.cardContentHTMList, (html: HTMLElement) => {
      html.classList.add('card-hide');
    });
  }

  selectCard(method: string): void {
    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('FundingComponent:selectCard', false);
    }

    if (method === 'checkMoneyOrder') {
      this.showAlertInfo('The countdown to your next big win starts soon!');
      return;
    }

    if (method === 'payNearMe') {
      this.payNearMe();
      return;
    }

    if (method === 'nhPlay') {
      this.showAlertMessage(
        'NHPlay Redirect',
        'NHPlay now requires all users to sign in directly to their site.' +
        'We appologize for the inconvience. Please clicl "Continue" to go to NHPlay.com'
      );
      return;
    }

    const depositSection = this.elementRef.nativeElement.querySelector(
      '.my-deposit-section'
    );


    const showCard = this.elementRef.nativeElement.querySelector(
      '.my-deposit-section .' + method + '.card-content'
    );
    const depositRow = this.elementRef.nativeElement.querySelectorAll(
      '.my-deposit-section .deposit-row'
    );


    const openChev = this.elementRef.nativeElement.querySelector(
      '.my-deposit-section .' + method + '.deposit-row .open-chev'
    );
    const closeChev = this.elementRef.nativeElement.querySelector(
      '.my-deposit-section .' + method + '.deposit-row .close-chev'
    );

    // open arrow
    if (closeChev.classList.contains('active-chev')) {
      // console.log('OPEN')
      depositSection.classList.add('active');
      closeChev.classList.remove('active-chev');
      openChev.classList.add('active-chev');

      // hide all
      _.forEach(this.cardContentHTMList, (cardContent: HTMLElement) => {
        cardContent.classList.add('card-hide');
      });

      // show showCard
      showCard.classList.remove('card-hide');

      depositRow.forEach((row: HTMLElement) => {
        row.classList.add('card-hide');

        if (row.dataset['name'] === showCard.dataset['name']) {
          row.classList.remove('card-hide');
        }
      });

      (this.controller as any)[method] = true;

    }
    // close arrow
    else {
      // console.log('CLOSE')
      depositSection.classList.remove('active');
      closeChev.classList.add('active-chev');
      openChev.classList.remove('active-chev');

      // hide all
      _.forEach(this.cardContentHTMList, (html: HTMLElement) => {
        html.classList.add('card-hide');
      });

      // show all
      depositRow.forEach((row: HTMLElement) => {
        row.classList.remove('card-hide');
      });

      (this.controller as any)[method] = false;
    }
  }

  payNearMe(): void {
    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('FundingComponent:payNearMe', false);
      return;
    }

    this.payNearMeComponent.openLink();
  }

  async showAlertMessage(hdr: string, msg: string): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: hdr,
      message: msg,
      buttons: [
        {
          text: 'Continue',
          handler: () => {
            window.open('https://nhplay.com/login.cfm', '_blank');
          }
        },
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {
          }
        }
      ]
    });

    await alert.present();
  }

  depositTab($event: any, type: string): void {
    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('FundingComponent:depositTab', false);
    }

    this.showDepositList = true;
    this.showWithdrawList = false;

    this.fundingTabList = this.elementRef.nativeElement.querySelectorAll(
      '.deposit-withdraw-row .tab'
    );
    _.forEach(this.fundingTabList, (html: HTMLElement) => {
      html.classList.remove('active');
    });

    const currentTab = this.elementRef.nativeElement.querySelector('.' + type + ' .tab');
    currentTab.classList.add('active');
  }

  withdrawTab($event: any, type: string): void {
    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('FundingComponent:withdrawTab', false);
    }

    if (this.affiliateName.toLowerCase() === 'nhplay')
      return;

    this.showWithdrawList = true;
    this.showDepositList = false;

    this.fundingTabList = this.elementRef.nativeElement.querySelectorAll(
      '.deposit-withdraw-row .tab'
    );
    _.forEach(this.fundingTabList, (html: HTMLElement) => {
      html.classList.remove('active');
    });

    const currentTab = this.elementRef.nativeElement.querySelector('.' + type + ' .tab');
    currentTab.classList.add('active');
  }

  cancel(): void {
    this.modalCtrl.dismiss();
  }

  close(): void {
    this.modalCtrl.dismiss();
  }

  async showAlertInfo(message: string): Promise<void> {
    this.showAlertInfoHandler = await this.alertCtrl.create({
     header: 'Info',
     message,
     buttons: [
       {
         text: 'Close',
       }
     ]
   });

   await this.showAlertInfoHandler.present();
 }

}
